import { useState } from 'react';
import Layout from '../components/Layout';
import { Link } from 'react-router-dom';

type ProjectStatus = 'needs-funding' | 'in-progress' | 'completed';
type ProjectType = 'Community Lab' | 'School Lab' | 'Mobile Lab';
type SchoolType = 'public' | 'private' | 'community';

type Project = {
  id: number;
  title: string;
  location: {
    city: string;
    state: string;
    coordinates: { lat: number; lng: number };
  };
  projectType: ProjectType;
  schoolType: SchoolType;
  needs: string[];
  status: ProjectStatus;
  progress: number;
  description: string;
  image: string;
  fundingGoal: number;
  fundingRaised: number;
  adminContact?: string;
  timeline?: string;
};

const projects: Project[] = [
  {
    id: 1,
    title: 'Ogun State Digital Learning Center',
    location: { city: 'Abeokuta', state: 'Ogun', coordinates: { lat: 7.1475, lng: 3.3619 } },
    projectType: 'Community Lab',
    schoolType: 'community',
    needs: ['Computers', 'Internet', 'Furniture'],
    status: 'in-progress',
    progress: 65,
    description: 'A community digital hub serving 5 local schools with shared computing resources and after-school programs.',
    image: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=600&q=80',
    fundingGoal: 25000,
    fundingRaised: 16250,
    adminContact: 'admin@ogundigital.org',
    timeline: 'Q2 2026'
  },
  {
    id: 2,
    title: 'Lagos Island Primary School Lab',
    location: { city: 'Lagos Island', state: 'Lagos', coordinates: { lat: 6.4541, lng: 3.4084 } },
    projectType: 'School Lab',
    schoolType: 'public',
    needs: ['Tablets', 'Software Licenses'],
    status: 'needs-funding',
    progress: 30,
    description: 'Bringing tablet-based learning to over 800 students in an underfunded public primary school.',
    image: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=600&q=80',
    fundingGoal: 15000,
    fundingRaised: 4500,
    adminContact: 'principal@lagosislandps.edu.ng',
    timeline: 'Q3 2026'
  },
  {
    id: 3,
    title: 'Kano Mobile Learning Unit',
    location: { city: 'Kano', state: 'Kano', coordinates: { lat: 12.0022, lng: 8.5920 } },
    projectType: 'Mobile Lab',
    schoolType: 'community',
    needs: ['Vehicle', 'Solar Panels', 'Laptops'],
    status: 'needs-funding',
    progress: 15,
    description: 'A solar-powered mobile unit bringing digital education to rural communities across Kano State.',
    image: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=600&q=80',
    fundingGoal: 40000,
    fundingRaised: 6000,
    adminContact: 'outreach@kanomobile.org',
    timeline: 'Q4 2026'
  },
  {
    id: 4,
    title: 'Enugu Tech Academy',
    location: { city: 'Enugu', state: 'Enugu', coordinates: { lat: 6.4584, lng: 7.5464 } },
    projectType: 'School Lab',
    schoolType: 'private',
    needs: ['Advanced Workstations', 'Coding Curriculum'],
    status: 'completed',
    progress: 100,
    description: 'A fully equipped coding academy providing advanced tech education to secondary students.',
    image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=600&q=80',
    fundingGoal: 35000,
    fundingRaised: 35000,
    timeline: 'Completed Q4 2025'
  },
  {
    id: 5,
    title: 'Ibadan Community Digital Hub',
    location: { city: 'Ibadan', state: 'Oyo', coordinates: { lat: 7.3775, lng: 3.9470 } },
    projectType: 'Community Lab',
    schoolType: 'community',
    needs: ['Computers', 'Internet', 'Admin Funding'],
    status: 'in-progress',
    progress: 50,
    description: 'Supporting digital literacy for youth and adults in underserved neighborhoods of Ibadan.',
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=600&q=80',
    fundingGoal: 20000,
    fundingRaised: 10000,
    adminContact: 'hub@ibadandigital.org',
    timeline: 'Q2 2026'
  },
  {
    id: 6,
    title: 'Port Harcourt Girls Tech Initiative',
    location: { city: 'Port Harcourt', state: 'Rivers', coordinates: { lat: 4.8156, lng: 7.0498 } },
    projectType: 'School Lab',
    schoolType: 'public',
    needs: ['Laptops', 'Internet', 'Building Renovation'],
    status: 'needs-funding',
    progress: 20,
    description: 'Empowering girls through technology education with a dedicated computer lab and mentorship program.',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80',
    fundingGoal: 30000,
    fundingRaised: 6000,
    adminContact: 'girls@phtechinitiative.org',
    timeline: 'Q3 2026'
  }
];

const statusLabels: Record<ProjectStatus, string> = {
  'needs-funding': 'Needs Funding',
  'in-progress': 'In Progress',
  'completed': 'Completed'
};

const Projects = () => {
  const [selectedStatus, setSelectedStatus] = useState<ProjectStatus | 'all'>('all');
  const [selectedState, setSelectedState] = useState<string>('all');
  const [selectedSchoolType, setSelectedSchoolType] = useState<SchoolType | 'all'>('all');
  const [expandedProject, setExpandedProject] = useState<number | null>(null);
  const [selectedMapProject, setSelectedMapProject] = useState<number | null>(null);

  const states = Array.from(new Set(projects.map(p => p.location.state)));

  const filteredProjects = projects.filter(project => {
    if (selectedStatus !== 'all' && project.status !== selectedStatus) return false;
    if (selectedState !== 'all' && project.location.state !== selectedState) return false;
    if (selectedSchoolType !== 'all' && project.schoolType !== selectedSchoolType) return false;
    return true;
  });

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case 'needs-funding': return '#e74c3c';
      case 'in-progress': return '#f39c12';
      case 'completed': return '#27ae60';
    }
  };

  return (
    <Layout>
      <section className="projects-hero">
        <h1>Projects: Digital Labs in Action</h1>
        <p className="projects-hero__intro">
          Discover where Pascaline is bringing digital learning to communities. See which schools 
          need support and how you can help build accessible labs for every student.
        </p>
      </section>

      {/* Interactive Map Section */}
      <section className="projects-map" aria-label="Project locations map">
        <div className="projects-map__container">
          <div className="projects-map__visual">
            <svg viewBox="0 0 800 600" className="projects-map__svg">
              {/* Simplified Nigeria Map Outline */}
              <path 
                d="M200 150 L350 100 L500 120 L600 180 L650 300 L600 450 L500 500 L350 480 L200 400 L150 280 Z" 
                fill="#e8f4f8" 
                stroke="#0b1f3a" 
                strokeWidth="2"
              />
              {/* Map pins for each project */}
              {projects.map((project) => {
                // Simplified coordinate mapping for visualization
                const pinPositions: Record<string, { x: number; y: number }> = {
                  'Ogun': { x: 280, y: 350 },
                  'Lagos': { x: 250, y: 380 },
                  'Kano': { x: 420, y: 150 },
                  'Enugu': { x: 480, y: 340 },
                  'Oyo': { x: 300, y: 320 },
                  'Rivers': { x: 450, y: 420 }
                };
                const pos = pinPositions[project.location.state] || { x: 400, y: 300 };
                const isSelected = selectedMapProject === project.id;
                
                return (
                  <g key={project.id} className="projects-map__pin-group">
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r={isSelected ? 18 : 12}
                      fill={getStatusColor(project.status)}
                      stroke="white"
                      strokeWidth="3"
                      className="projects-map__pin"
                      onClick={() => setSelectedMapProject(isSelected ? null : project.id)}
                      style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                    />
                    <text
                      x={pos.x}
                      y={pos.y + 4}
                      textAnchor="middle"
                      fill="white"
                      fontSize="10"
                      fontWeight="bold"
                      style={{ pointerEvents: 'none' }}
                    >
                      {project.id}
                    </text>
                  </g>
                );
              })}
            </svg>
            
            {/* Map Legend */}
            <div className="projects-map__legend">
              <div className="projects-map__legend-item">
                <span className="projects-map__legend-dot" style={{ background: '#e74c3c' }}></span>
                Needs Funding
              </div>
              <div className="projects-map__legend-item">
                <span className="projects-map__legend-dot" style={{ background: '#f39c12' }}></span>
                In Progress
              </div>
              <div className="projects-map__legend-item">
                <span className="projects-map__legend-dot" style={{ background: '#27ae60' }}></span>
                Completed
              </div>
            </div>
          </div>

          {/* Selected Project Preview */}
          {selectedMapProject && (
            <div className="projects-map__preview">
              {(() => {
                const project = projects.find(p => p.id === selectedMapProject);
                if (!project) return null;
                return (
                  <>
                    <button 
                      className="projects-map__preview-close"
                      onClick={() => setSelectedMapProject(null)}
                      aria-label="Close preview"
                    >
                      ×
                    </button>
                    <h3>{project.title}</h3>
                    <p className="projects-map__preview-location">
                      📍 {project.location.city}, {project.location.state}
                    </p>
                    <p className="projects-map__preview-desc">{project.description}</p>
                    <div className="projects-map__preview-progress">
                      <div 
                        className="projects-map__preview-progress-bar"
                        style={{ width: `${project.progress}%`, background: getStatusColor(project.status) }}
                      />
                    </div>
                    <p className="projects-map__preview-funding">
                      ${project.fundingRaised.toLocaleString()} / ${project.fundingGoal.toLocaleString()} raised
                    </p>
                    <button 
                      className="projects-map__preview-btn"
                      onClick={() => {
                        setExpandedProject(project.id);
                        setSelectedMapProject(null);
                      }}
                    >
                      View Full Details
                    </button>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </section>

      {/* Filters Section */}
      <section className="projects-filters" aria-label="Filter projects">
        <div className="projects-filters__group">
          <label htmlFor="filter-status">Status:</label>
          <select 
            id="filter-status" 
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as ProjectStatus | 'all')}
          >
            <option value="all">All Statuses</option>
            <option value="needs-funding">Needs Funding</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div className="projects-filters__group">
          <label htmlFor="filter-state">State:</label>
          <select 
            id="filter-state"
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
          >
            <option value="all">All States</option>
            {states.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </div>
        <div className="projects-filters__group">
          <label htmlFor="filter-school">School Type:</label>
          <select 
            id="filter-school"
            value={selectedSchoolType}
            onChange={(e) => setSelectedSchoolType(e.target.value as SchoolType | 'all')}
          >
            <option value="all">All Types</option>
            <option value="public">Public</option>
            <option value="private">Private</option>
            <option value="community">Community</option>
          </select>
        </div>
        <div className="projects-filters__count">
          Showing {filteredProjects.length} of {projects.length} projects
        </div>
      </section>

      {/* Projects Grid */}
      <section className="projects-grid" aria-label="Project listings">
        {filteredProjects.map((project) => (
          <article 
            key={project.id} 
            className={`project-card ${expandedProject === project.id ? 'project-card--expanded' : ''}`}
          >
            <div className="project-card__image">
              <img src={project.image} alt={project.title} />
              <span 
                className="project-card__status"
                style={{ background: getStatusColor(project.status) }}
              >
                {statusLabels[project.status]}
              </span>
              <span className="project-card__type">{project.projectType}</span>
            </div>
            
            <div className="project-card__content">
              <h3 className="project-card__title">{project.title}</h3>
              <p className="project-card__location">
                📍 {project.location.city}, {project.location.state}
              </p>
              <p className="project-card__description">{project.description}</p>
              
              <div className="project-card__needs">
                <strong>Needs:</strong>
                <div className="project-card__needs-tags">
                  {project.needs.map(need => (
                    <span key={need} className="project-card__need-tag">{need}</span>
                  ))}
                </div>
              </div>
              
              <div className="project-card__progress">
                <div className="project-card__progress-header">
                  <span>Progress</span>
                  <span>{project.progress}%</span>
                </div>
                <div className="project-card__progress-bar-container">
                  <div 
                    className="project-card__progress-bar"
                    style={{ 
                      width: `${project.progress}%`,
                      background: getStatusColor(project.status)
                    }}
                  />
                </div>
                <p className="project-card__funding">
                  ${project.fundingRaised.toLocaleString()} raised of ${project.fundingGoal.toLocaleString()} goal
                </p>
              </div>

              {/* Expanded Details */}
              {expandedProject === project.id && (
                <div className="project-card__expanded-details">
                  {project.timeline && (
                    <p><strong>Timeline:</strong> {project.timeline}</p>
                  )}
                  {project.adminContact && (
                    <p><strong>Contact:</strong> <a href={`mailto:${project.adminContact}`}>{project.adminContact}</a></p>
                  )}
                  <p><strong>School Type:</strong> {project.schoolType.charAt(0).toUpperCase() + project.schoolType.slice(1)}</p>
                </div>
              )}
              
              <div className="project-card__actions">
                <a href="#donate" className="project-card__btn project-card__btn--primary">
                  Donate
                </a>
                <a href="#volunteer" className="project-card__btn project-card__btn--secondary">
                  Volunteer
                </a>
                <button 
                  className="project-card__btn project-card__btn--outline"
                  onClick={() => setExpandedProject(expandedProject === project.id ? null : project.id)}
                >
                  {expandedProject === project.id ? 'Show Less' : 'Learn More'}
                </button>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* Call to Action */}
      <section className="projects-cta">
        <h2>Want to Start a Project in Your Community?</h2>
        <p>
          We're always looking for partners to bring digital education to more students. 
          Whether you're a school administrator, community leader, or organization, 
          let's work together to build the next digital lab.
        </p>
        <div className="projects-cta__actions">
          <Link to="/contact" className="projects-cta__btn projects-cta__btn--primary">
            Partner With Us
          </Link>
          <a href="#apply" className="projects-cta__btn projects-cta__btn--secondary">
            Submit a Project Proposal
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default Projects;
